import { Autocomplete, Drawer, Grid, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppState, useAppDispatch, useAppSelector } from "../../store";
import {
  setIsApproved,
  setIsEditUser,
  setOpenUserModal,
  setSelectUserData,
} from "../../store/slices/usersSlice";
import { useRoles } from "../../hooks/useRoles";
import { useUsers } from "../../hooks/useUsers";
import { useSnackbar } from "../../hooks/useSnackbar";
import { AlertDialog } from "../common/AlertDailog";
import {
  userCreationSchema,
  userUpdateSchema,
} from "../../config/schemas/userSchema";
import { setUserObj, setUserPermissions } from "../../store/slices/commonSlice";
import useRegisterFields from "../../pages/RegisterPage/useRegisterFields";
import DynamicForm from "../common/DynamicForm";
import { useGetChangedFields } from "../../hooks/useGetChangedFields";
import AddReasonForm from "../common/AddReasonForm";
import { usePermissionWrapper } from "../../hooks/usePermissionWrapper";
import ESignAuth from "../common/ESign";

const UserModal = (props: any) => {
  const { getAllUsers, paginationModel } = props;
  const isEdit = useAppSelector(
    (state: AppState) => state.usersSlice.isEditUser
  );
  const isApproved = useAppSelector(
    (state: AppState) => state.usersSlice.isApproved
  );
  const selectUserDataDetails = useAppSelector(
    (state: AppState) => state.usersSlice.selectUserData
  );

  const userObj = useAppSelector(
    (state: AppState) => state.commonSlice.userObj
  );

  const { checkPermission } = usePermissionWrapper();
  const { getAllUIPermissions, uIPermissionData, uIPermissionIsSuccess } =
    useUsers();

  const initialValue = {
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    default_role: "",
    roles: [],
  };

  const [approveUserDetails, setApproveUserDetails] = useState<any>({});
  const [openESignDialog, setOpenESignDialog] = useState(false);

  const values = isEdit || isApproved ? selectUserDataDetails : initialValue;
  const schemas = isEdit || isApproved ? userUpdateSchema : userCreationSchema;
  type FormData = z.infer<typeof schemas>;
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: values,
    resolver: zodResolver(schemas),
  });

  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState<any[]>([]);
  const [selectedRole, setSelectedRole] = useState<any[]>(
    isEdit ? [...selectUserDataDetails.roles] : []
  );
  const watchRoles = watch("roles", roles);
  const watchDefaultRole = watch("default_role");
  const { showSuccess, handleError } = useSnackbar();
  useEffect(() => {
    if (roles?.length && !watchRoles.includes(watchDefaultRole)) {
      setValue("default_role", "");
    }
  }, [watchRoles]);
  const { getAllRoles, allRolesData } = useRoles();
  const {
    createUser,
    createUserSuccess,
    createUserData,
    createUserError,
    createUserIsError,
    updateUser,
    updateUserSuccess,
    updateUserData,
    updateUserIsError,
    updateUserError,
    approveUser,
    approveUserSuccess,
    approveUserData,
    approveUserIsError,
    approveUserError,
  } = useUsers();

  const openUsersModal = useAppSelector(
    (state: AppState) => state.usersSlice?.openUsersModal
  );

  const handleCreateUser = (data: any) => {
    createUser({
      data: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        password: data.password,
        default_role: data.default_role,
        roles: data.roles,
      },
    });
  };

  const [updatedUserDetails, setUpdatedUserDetails] = useState({});

  const handleEditUser = async (data: any, updatedDataObj?: any) => {
    await updateUser({
      data: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        roles: data.roles,
        default_role: data.default_role,
        updated_fields: updatedDataObj || [],
      },
    });
    const selectedRole = allRolesData?.result?.find(
      (item: any) => item.code == data.default_role
    ).name;
    if (userObj.defaultRole != selectedRole) {
      getAllUIPermissions({});
    }
    if (data.email == userObj.email) {
      setUpdatedUserDetails({
        first_name: data.first_name,
        last_name: data.last_name,
        defaultRole: selectedRole,
      });
    }
  };

  useEffect(() => {
    if (uIPermissionIsSuccess) {
      dispatch(setUserPermissions(uIPermissionData));
    }
  }, [uIPermissionData, uIPermissionIsSuccess]);

  const handleApprovedUser = (data: any) => {
    approveUser({
      email: approveUserDetails?.email,
      data: {
        roles: approveUserDetails.roles,
        default_role: approveUserDetails.default_role,
        reason: data?.sign_reason?.code_list_value,
        reason_code: "ESIGN_REASON",
        reason_value_code: data?.sign_reason?.code_list_value_code,
        password: data.password,
      },
    });
  };

  const submit = (data: FormData) => {
    isEdit
      ? handleEditUser(data)
      : isApproved
      ? onAppoveBtnClick(data)
      : handleCreateUser(data);
  };
  useEffect(() => {
    if (createUserSuccess || updateUserSuccess || approveUserSuccess) {
      const successMsg = createUserData ?? updateUserData ?? approveUserData;
      getAllUsers(0, paginationModel.pageSize);
      updateUserSuccess &&
        dispatch(setUserObj({ ...userObj, ...updatedUserDetails }));
      const id = createUserSuccess
        ? "user-create-success"
        : updateUserSuccess
        ? "user-update-success"
        : "user-approve-success";
      showSuccess(successMsg?.details, id);
      handleClose();
    }
  }, [createUserSuccess, updateUserSuccess, approveUserSuccess]);

  useEffect(() => {
    if (createUserIsError || updateUserIsError || approveUserIsError) {
      const errorMsg: any =
        createUserError ?? updateUserError ?? approveUserError;
      handleError(errorMsg);
    }
  }, [createUserIsError, updateUserIsError, approveUserIsError]);

  const onInvalid = (errors: any) => console.debug(errors);

  const handleClose = () => {
    dispatch(setIsEditUser(false));
    dispatch(setIsApproved(false));
    dispatch(setSelectUserData({}));
    dispatch(setOpenUserModal(false));
    reset();
  };

  const getAllRolesFunc = () => {
    getAllRoles({});
  };

  useEffect(() => {
    if (openUsersModal) {
      getAllRolesFunc();
    }
  }, [openUsersModal]);

  useEffect(() => {
    if (allRolesData && openUsersModal) {
      const data = allRolesData?.result?.map((ele: any) => ele?.code);
      setRoles(data);
    }
  }, [openUsersModal, allRolesData]);

  const { registerFields } = useRegisterFields({
    userPage: true,
    isEdit,
    isApproved,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setDrawerOpen(open);
    };

  const fieldsJson = [
    ...registerFields,
    { name: "roles", label: "Roles" },
    { name: "default_role", label: "Default Role" },
  ];
  const editedData: any = useGetChangedFields(
    control,
    watch,
    fieldsJson,
    getValues,
    values
  );

  const handleAddReason = (updatedDataObj: any) => {
    handleEditUser(getValues(), updatedDataObj);
  };

  const onAppoveBtnClick = (data: any) => {
    setOpenESignDialog(true);
    setApproveUserDetails(data);
  };

  return (
    <>
      <AlertDialog
        open={openUsersModal}
        title={isEdit ? `Edit User` : isApproved ? `Approve User` : `Add User`}
        handleClose={handleClose}
        handleConfirm={
          isEdit && !checkPermission("OVERRIDE_AUDIT_REASON")
            ? handleSubmit(toggleDrawer(true))
            : handleSubmit(submit, onInvalid)
        }
        cancelBtn="CANCEL"
        confirmBtn={isApproved ? `Approve` : `Save`}
        maxWidth="md"
        saveDisable={!isApproved && editedData?.length == 0}
      >
        <form onSubmit={handleSubmit(submit, onInvalid)}>
          <DynamicForm
            fieldsJson={registerFields}
            errors={errors}
            register={register}
            control={control}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="roles"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    id="roles"
                    multiple
                    options={roles ?? []}
                    getOptionLabel={(option: any) => option}
                    value={value}
                    onChange={(event, newValue) => {
                      onChange(newValue ?? "");
                      setSelectedRole([...newValue]);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Roles"
                        required
                        variant="standard"
                        error={!!error}
                        helperText={error ? error.message : null}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="default_role"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    id="default_role"
                    options={selectedRole}
                    value={value}
                    onChange={(event, newValue) => {
                      onChange(newValue ?? "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Default Role"
                        variant="standard"
                        required
                        error={!!error}
                        helperText={error ? error.message : null}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
        <div
          id="drawer-container"
          style={{ display: drawerOpen ? "block" : "none" }}
        />
        {drawerOpen && (
          <Drawer
            anchor="bottom"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            style={{ zIndex: "1300" }}
            PaperProps={{ style: { position: "absolute" } }}
            ModalProps={{
              container: document.getElementById("drawer-container"),
              style: { position: "absolute", background: "rgba(0,0,0,0.3)" },
            }}
          >
            <AddReasonForm
              toggleDrawer={toggleDrawer}
              editedData={editedData}
              handleAddReason={handleAddReason}
            />
          </Drawer>
        )}
      </AlertDialog>

      {openESignDialog && (
        <ESignAuth
          openESignDialog={openESignDialog}
          setOpenESignDialog={setOpenESignDialog}
          codes={"ESIGN_REASON"}
          handleEsignConfirm={handleApprovedUser}
        />
      )}
    </>
  );
};

export default UserModal;
